// ServingsStepper — the one servings control, reused on every surface
// that scales a recipe (pre-cook preview, in-cook overview, schedule
// picker, meal drawer). Replaces four bespoke −/+ steppers that each
// hard-capped at 20 with no way to type a value.
//
// Token-agnostic by design: the call site passes a `palette` so the
// control inherits whichever design-token system that screen already
// uses (lib/tokens COLOR/FONT or mcmTokens color/font) instead of this
// component reaching into one of them. Renders:
//
//     [−]  [ 24 ]  [+]   ×2  ×3
//
// The number is a real text input — tap to type "48" directly. −/+
// step by one; ×2 / ×3 jump by a multiple (capped at MAX_SERVINGS).

import { useEffect, useRef, useState } from "react";
import {
  MAX_SERVINGS,
  MIN_SERVINGS,
  SERVINGS_QUICK_MULTIPLIERS,
  clampServings,
} from "../lib/recipeConstants";

const DEFAULT_PALETTE = {
  surface: "rgba(255,255,255,0.62)",   // number-field background
  btnBg: "rgba(255,255,255,0.78)",     // stepper button background
  btnBorder: "rgba(30,30,30,0.14)",
  accent: "#D4A637",                   // +/− glyph + active multiplier
  accentSoft: "rgba(212,166,55,0.18)", // multiplier fill
  ink: "#1E1E1E",                      // the number itself
  muted: "#5A5A5A",                    // label + disabled
  fontLabel: "inherit",
  fontNum: "inherit",
};

export function ServingsStepper({
  value,
  onChange,                                 // (n:int) => void — receives clamped value
  min = MIN_SERVINGS,
  max = MAX_SERVINGS,
  multipliers = SERVINGS_QUICK_MULTIPLIERS,
  palette,
  label = null,                             // optional inline kicker (e.g. "SERVES")
  ariaLabel = "Servings",
}) {
  const p = { ...DEFAULT_PALETTE, ...(palette || {}) };
  const cur = clampServings(value, max);

  // The field is controlled by a local draft string so the user can
  // clear it and type freely ("" → "4" → "48") without each keystroke
  // being clamped to 1. We mirror the external value into the draft
  // ONLY when the user isn't actively editing — an `editing` ref guards
  // against a parent re-render (or our own onChange echo) stomping
  // in-flight typing, the same controlled-input echo trap that makes
  // debounced fields "delete characters" as you type.
  const [draft, setDraft] = useState(String(cur));
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) setDraft(String(clampServings(value, max)));
  }, [value, max]);

  const commit = (raw) => {
    const n = clampServings(raw === "" ? min : raw, max);
    setDraft(String(n));
    if (n !== cur) onChange(n);
  };
  const bump = (n) => {
    const next = clampServings(n, max);
    setDraft(String(next));
    if (next !== cur) onChange(next);
  };

  const stepBtn = (disabled) => ({
    width: 30, height: 30, borderRadius: 15, flexShrink: 0,
    background: p.btnBg, border: `1px solid ${p.btnBorder}`,
    color: disabled ? p.muted : p.accent,
    fontSize: 18, fontWeight: 600, lineHeight: 1, padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1, touchAction: "manipulation",
  });

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
      {label && (
        <span style={{
          fontFamily: p.fontLabel, fontSize: 12, fontWeight: 600,
          color: p.muted, letterSpacing: "0.12em",
        }}>
          {label}
        </span>
      )}
      <button
        type="button"
        className="mcm-focusable"
        onClick={() => bump(cur - 1)}
        disabled={cur <= min}
        aria-label="Fewer servings"
        style={stepBtn(cur <= min)}
      >−</button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={draft}
        aria-label={ariaLabel}
        onFocus={(e) => { editing.current = true; try { e.target.select(); } catch (_) { /* noop */ } }}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
        onBlur={(e) => { editing.current = false; commit(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { editing.current = false; commit(e.currentTarget.value); e.currentTarget.blur(); }
          else if (e.key === "ArrowUp") { e.preventDefault(); bump(cur + 1); }
          else if (e.key === "ArrowDown") { e.preventDefault(); bump(cur - 1); }
        }}
        style={{
          width: 46, textAlign: "center",
          fontFamily: p.fontNum, fontSize: 15, fontWeight: 700, color: p.ink,
          background: p.surface, border: `1px solid ${p.btnBorder}`,
          borderRadius: 8, padding: "5px 4px", outline: "none",
          MozAppearance: "textfield",
        }}
      />
      <button
        type="button"
        className="mcm-focusable"
        onClick={() => bump(cur + 1)}
        disabled={cur >= max}
        aria-label="More servings"
        style={stepBtn(cur >= max)}
      >+</button>
      {(multipliers || []).map((m) => {
        const target = cur * m;
        // Clamp, don't disable: ×2 at 72 lands on the 100 cap, same as
        // holding +. Dead multiplier buttons while + still works read
        // as broken. Only gray out once there's nothing left to gain.
        const disabled = cur >= max;
        return (
          <button
            key={m}
            type="button"
            className="mcm-focusable"
            onClick={() => bump(Math.min(target, max))}
            disabled={disabled}
            aria-label={`Multiply servings by ${m}`}
            style={{
              fontFamily: p.fontLabel, fontSize: 12, fontWeight: 700,
              padding: "6px 9px", borderRadius: 8, flexShrink: 0,
              background: disabled ? p.btnBg : p.accentSoft,
              border: `1px solid ${disabled ? p.btnBorder : p.accent}`,
              color: disabled ? p.muted : p.accent,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.45 : 1,
              whiteSpace: "nowrap", touchAction: "manipulation",
            }}
          >×{m}</button>
        );
      })}
    </div>
  );
}

export default ServingsStepper;
